

import { LogoutButton } from "../../Buttons/LogoutButton";
import { Logo } from "./Logo";
import { ConnectionDot } from "./ConnectionDot";
import { IconAlertTriangle } from "@tabler/icons-react";
import { SM_UP_QUERY, useMediaQuery } from "@/hooks/useMediaQuery";

type AppBarStrippedProps = {
  error?: string;
  logo?: boolean;
};

export const AppBarStripped: React.FC<AppBarStrippedProps> = ({
  error,
  logo = true,
}) => {
  // Phones get an icon-only Logout so the message keeps its two lines: a
  // 26-char label ("Feuch ri clàrachadh a-mach", 178px) next to the dot left
  // the message 24px at 320px. Same breakpoint string as the `sm:` classes
  // below, so the JS gate and the CSS cannot drift.
  const smUp = useMediaQuery(SM_UP_QUERY);
  return (
    <>
      <div className="h-12" />
      {/* Three grid columns — logo / message / dot + Logout — so the message
          can only ever take what the other two leave and never slides under
          them: the centred-absolute pill this replaces budgeted 11rem for the
          right group, which only the English "Try logging out" fits. Phones
          drop the logo column while a message shows: 70px of branding was the
          difference between "Dh'fh…" and the whole sentence at 375px. */}
      <div className="fixed z-20 h-12 top-0 right-0 left-0 w-full grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 transition-all duration-300 border-b outline-quaternary surface-primary px-5">
        <div className={`py-2 ${error ? "hidden sm:block" : ""}`}>
          {logo ? <Logo /> : null}
        </div>
        {error ? (
          <div className="flex justify-center min-w-0">
            {/* Two clamped lines fit the h-12 bar at snug leading (2 × 16.5 +
                8 ≤ 48 on phones, 2 × 19.25 + 8 on sm+); `title` hands desktop
                hover whatever the clamp cut — on phones the second line is
                the reveal, there is no hover. `break-words`: the clamp only
                ellipsises vertical overflow, so a word wider than the column
                was cut mid-word with no "…" unless it may wrap. The pill's
                own text token: `font-error` is 2.6:1 on `surface-error`. */}
            <div
              title={error}
              className="flex items-center gap-1.5 sm:gap-2 py-1 px-2 sm:px-3 rounded-lg surface-error min-w-0"
            >
              <IconAlertTriangle
                size={16}
                className="text-[var(--color-font-error-on-surface)] shrink-0"
              />
              <span className="text-[var(--color-font-error-on-surface)] font-medium text-xs sm:text-sm leading-snug tracking-[0.005em] line-clamp-2 break-words min-w-0">
                {error}
              </span>
            </div>
          </div>
        ) : null}
        {/* SAppBar swaps the whole bar for this strip when initialiseUserBase
            fails — which is exactly what a network outage does. The dot lives
            here too, so "red" is visible in the one state that matters most;
            while the connection is fine it renders nothing (its sr-only live
            region is absolutely positioned and takes no gap). Pinned to the
            last column: without a message the group would otherwise
            auto-place into the empty middle one on sm+. No vertical padding:
            the 40px icon button must fit the 48px row. */}
        <div className="flex items-center gap-2 justify-self-end sm:col-start-3">
          <ConnectionDot />
          {error ? <LogoutButton size="small" iconOnly={!smUp} /> : null}
        </div>
      </div>
    </>
  );
};
